/**
 * Abarbeiten der Warteschlange.
 *
 * Grundsatz: die Reihenfolge bleibt erhalten. Ein Abschluss darf nicht vor
 * den Befunden ankommen, die er abschließt – deshalb wird streng
 * hintereinander gesendet und beim ersten Netzfehler abgebrochen.
 *
 * Zur Wiederholbarkeit: Befunde gehen per PUT und sind damit gefahrlos
 * mehrfach absendbar. Fotos gehen per POST, aber der Server erkennt ein
 * doppelt geschicktes Bild an seiner Prüfsumme und legt keinen zweiten
 * Eintrag an. Ein abgebrochener Sendevorgang kann also ohne Nachdenken
 * wiederholt werden.
 */

import { ApiFehler, api } from '../api/klient'
import {
  auftragAktualisieren,
  auftragErledigt,
  offeneAuftraege,
  wartendesFoto,
  type Auftrag,
} from './db'

export type Sendestand = {
  laeuft: boolean
  offen: number
  gescheitert: number
  /** Zuletzt aufgetretener Fehler, für den Hinweis in der Werkzeugleiste. */
  meldung?: string
}

type Horcher = (stand: Sendestand) => void

const horcher = new Set<Horcher>()
let laeuft = false
let letzteMeldung: string | undefined

export function beobachten(fn: Horcher): () => void {
  horcher.add(fn)
  void melden()
  return () => horcher.delete(fn)
}

async function melden(): Promise<void> {
  const alle = await offeneAuftraege()
  const stand: Sendestand = {
    laeuft,
    offen: alle.filter((a) => !a.fehler).length,
    gescheitert: alle.filter((a) => a.fehler).length,
    meldung: letzteMeldung,
  }
  horcher.forEach((fn) => fn(stand))
}

/**
 * Ein einzelner Auftrag.
 *
 * Rückgabe `true` heißt erledigt, `false` heißt: abbrechen und später erneut
 * versuchen (Netz weg). Ein endgültig abgelehnter Auftrag wird markiert und
 * gilt als erledigt, damit er die Schlange nicht blockiert.
 */
async function senden(auftrag: Auftrag): Promise<boolean> {
  try {
    if (auftrag.art === 'foto') {
      const foto = auftrag.foto_schluessel
        ? await wartendesFoto(auftrag.foto_schluessel)
        : undefined
      if (!foto) {
        // Bild ist verschwunden – der Auftrag ist gegenstandslos.
        await auftragErledigt(auftrag.id!, auftrag.foto_schluessel)
        return true
      }
      const formular = new FormData()
      formular.append('datei', foto.blob, 'aufnahme.jpg')
      formular.append('art', foto.art)
      if (foto.befund_id) formular.append('geraetebefund_id', foto.befund_id)
      await api.hochladen(auftrag.pfad, formular)
    } else if (auftrag.methode === 'PUT') {
      await api.setzen(auftrag.pfad, auftrag.koerper)
    } else if (auftrag.methode === 'PATCH') {
      await api.aendern(auftrag.pfad, auftrag.koerper)
    } else if (auftrag.methode === 'DELETE') {
      await api.loeschen(auftrag.pfad)
    } else {
      await api.senden(auftrag.pfad, auftrag.koerper)
    }

    await auftragErledigt(auftrag.id!, auftrag.foto_schluessel)
    return true
  } catch (fehler) {
    if (fehler instanceof ApiFehler) {
      // 401: die Sitzung ist abgelaufen. Nicht als Fehler markieren – nach
      // erneuter Anmeldung soll derselbe Auftrag durchgehen.
      if (fehler.status === 401 || fehler.status === 429) {
        letzteMeldung =
          fehler.status === 401 ? 'Bitte neu anmelden' : 'Server bremst – gleich erneut'
        return false
      }

      // 409 beim Abschluss: die Prüfung ist schon abgeschlossen. Damit ist
      // das Ziel erreicht, der Auftrag kann weg.
      if (fehler.status === 409 && auftrag.art === 'abschluss') {
        await auftragErledigt(auftrag.id!, auftrag.foto_schluessel)
        return true
      }

      // Alles andere ist ein fachlicher Fehler: erneutes Senden ändert nichts.
      // Markieren statt löschen, damit der Prüfer sieht, was nicht ankam.
      await auftragAktualisieren({
        ...auftrag,
        versuche: auftrag.versuche + 1,
        fehler: fehler.message,
      })
      letzteMeldung = fehler.message
      return true
    }

    // Netzfehler: Schlange stehen lassen und abbrechen.
    letzteMeldung = 'Keine Verbindung – Änderungen warten'
    await auftragAktualisieren({ ...auftrag, versuche: auftrag.versuche + 1 })
    return false
  }
}

/**
 * Arbeitet die Schlange ab, solange es geht.
 *
 * Mehrfache Aufrufe überlappen nicht: der zweite kehrt sofort zurück. Sonst
 * könnten zwei Durchläufe denselben Auftrag gleichzeitig senden.
 */
export async function abarbeiten(): Promise<void> {
  if (laeuft) return
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    letzteMeldung = 'Kein Netz – Änderungen warten'
    await melden()
    return
  }

  laeuft = true
  letzteMeldung = undefined
  await melden()

  try {
    for (const auftrag of await offeneAuftraege()) {
      if (auftrag.fehler) continue // schon endgültig abgelehnt
      const weiter = await senden(auftrag)
      await melden()
      if (!weiter) break
    }
  } finally {
    laeuft = false
    await melden()
  }
}

/** Einen abgelehnten Auftrag verwerfen. */
export async function verwerfen(id: number, fotoSchluessel?: string): Promise<void> {
  await auftragErledigt(id, fotoSchluessel)
  await melden()
}

/**
 * Sendeversuche an die Verbindung koppeln.
 *
 * `online` feuert, sobald das Betriebssystem wieder eine Verbindung sieht;
 * `visibilitychange` deckt den häufigeren Fall ab, dass der Prüfer das Handy
 * aus der Tasche holt und die App in den Vordergrund kommt.
 */
export function anVerbindungKoppeln(): () => void {
  const versuchen = () => void abarbeiten()

  window.addEventListener('online', versuchen)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') versuchen()
  })

  // Regelmäßig nachfassen: auf einem Spielplatz wechselt der Empfang, ohne
  // dass ein `online`-Ereignis ausgelöst wird.
  const takt = window.setInterval(versuchen, 30_000)

  versuchen()

  return () => {
    window.removeEventListener('online', versuchen)
    window.clearInterval(takt)
  }
}
